import { Container, Host } from "@reporting/rp-app-host";
import { Resource } from "@reporting/rp-types/ext";
import { ReportContext } from "@reporting/rp-types/analytics";
import { property } from "lit/decorators.js";
import { Engine, ExtensionEvent } from "../engine/engine";
import { ExtensionDefinition } from "../types";

export abstract class ExtensionHost extends Host {

	@property({type: Object})
	container: Container;

	@property({type: Object})
	resource: Resource;

	@property({ type: Object })
	reportContext: ReportContext;

	private _hostElement: HTMLElement;
	get hostElement() { return this._hostElement }

	private _extensionDefinition: ExtensionDefinition;
	async getExtensionDefinition() {
		if (this._extensionDefinition) return this._extensionDefinition;
		this._extensionDefinition = await this.container.ms('extensions').get<ExtensionDefinition>(`/definition/${this.idProject}`);
		return this._extensionDefinition;
	}

	private _mainResourceContent: string;

	private _engine: Engine;
	get engine() { return this._engine }

	get router() { return this.container?.router }

	get idProject() {
		if (this.resource) return this.resource.id_custom_page;
		const id = this.router?.path[0];
		return isNaN(Number(id)) ? null : Number(id);
	}

	get idResource() {
		if (this.resource) return this.resource.id_custom_resource;
		return this.router?.path[1];
	}

	reload(mainResourceContent?: string) {
		this._engine = null;
		this._mainResourceContent = mainResourceContent;
	}

	refresh(mainResourceContent?: string) {
		this.engine?.refresh(mainResourceContent);
	}

	/**
	 * Should be called only once during extension initialization;
	 */
	async loadExtension() {
		if (!this.idProject) return;

		const extension = await this.getExtensionDefinition();
		if (!extension) return;

		if (this.resource?.id_custom_resource) {
			// set different main resource
			extension.project.id_custom_resource = this.resource.id_custom_resource;
		}

		const path = [...this.router.path];
		path.shift();

		this._engine = new Engine(extension, {
			staticUrl: this.container.options.staticUrl,
			service: this.container.ms('extensions'),
			path: path.join('/'),
			queryParams: this.router.queryParams,
			context: this.reportContext,
			idResource: this.resource?.id_custom_resource
		});

		this._engine.addEventListener('extension.load', (e: ExtensionEvent) => {
			this.dispatchEvent(new ExtensionEvent('load', { engine: this._engine }));
		});

		this._engine.addEventListener('extension.detail', (e: ExtensionEvent) => {
			this.dispatchEvent(new ExtensionEvent('detail', { engine: this._engine, data: e.data }));
		});

		if (this._mainResourceContent != null) this._engine.setMainResource(this._mainResourceContent);

		this._engine.run(this.hostElement);
	}

	async updateExtension() {
		if (!this.engine) return;
		this.engine.run(this.hostElement);
	}

	async updated() {
		if (!this.container || this._engine) return;
		await this.loadExtension();
	}

	dispose() {
		this.engine?.dispose();
	}

}
